import { Suspense, lazy, useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { PLANS, TERMS } from '@pageboss/shared';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import type { SiteDetailResponse } from '@/lib/types';

// =====================================================================
// ReputationSettings — "/app/b/:siteId/reputation/settings" (WP1 Task 9).
// Two pieces, neither previously mounted anywhere in the new panel: the
// review link + QR card (components/review-link-card.tsx, orphaned since
// site-detail.tsx was replaced by overview.tsx in Task 6 — no tier gate,
// design comment there: "Every tier gets this") and a plain-language
// explainer of how review replies work (the approval rule, honestly — see
// component comment below for why the copy is worded the way it is).
// =====================================================================

// Same code-splitting reason as the pre-move site-detail.tsx: the `qrcode`
// canvas encoder (~30KB) should never land in the main dashboard chunk —
// most sessions never open this screen.
const ReviewLinkCard = lazy(() => import('@/components/review-link-card'));

export function ReputationSettings() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();

  const [siteName, setSiteName] = useState<string | null>(null);
  const [reviewPlaceId, setReviewPlaceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    void api
      .get<SiteDetailResponse>(locationApi(businessId, siteId, ''))
      .then((res) => {
        setSiteName(res.site.name);
        setReviewPlaceId(res.reviewPlaceId);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load site.'));
  }, [businessId, siteId]);

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Reputation"
        title="Reputation settings"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready' || !siteId) return null;

  return (
    <PageContainer>
      <PageHeader eyebrow="Reputation" title="Reputation settings" />

      {error ? (
        <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg" role="alert">
          {error}
        </div>
      ) : null}

      {reviewPlaceId && siteName ? (
        <Suspense fallback={<Skeleton className="h-64 w-full rounded-xl" />}>
          <ReviewLinkCard placeId={reviewPlaceId} businessName={siteName} />
        </Suspense>
      ) : reviewPlaceId === null && siteName ? (
        <section className="rounded-lg border border-border-subtle bg-card p-6 text-sm text-text-tertiary">
          Connect your {TERMS.googleProfile} (or set your address on Business info) so we can find
          your Google listing — the review link and QR code show up here once we know it.
        </section>
      ) : null}

      <ReplyRulesCard />
    </PageContainer>
  );
}

// The human-approval rule, worded plainly and without implying anything
// autopilots (CLAUDE.md Q14 / decision #18 extends the same "never
// autopilot" rule to review replies). No credit figures (#11), tier names
// come from PLANS labels (#20) rather than a hand-typed string.
function ReplyRulesCard() {
  const replyTiers = `${PLANS.business.label} and ${PLANS.growth.label}`;
  return (
    <section className="rounded-lg border border-border-subtle bg-card p-6">
      <h2 className="text-lg font-semibold tracking-tight">How review replies work</h2>
      <ul className="mt-3 flex flex-col gap-3 text-sm leading-relaxed text-text-secondary">
        <li>
          From the Reviews & replies screen, AI can draft a reply to any Google review — or you can
          write one yourself.
        </li>
        <li>
          <strong className="font-medium text-foreground">Nothing posts without your OK.</strong> A
          draft sits in an editable box until you click "Approve &amp; send" — that's the only thing
          that ever reaches Google.
        </li>
        <li>Replying to reviews is included on {replyTiers}.</li>
      </ul>
    </section>
  );
}

export default ReputationSettings;
